// ===========================================================================
// Shared sign-in / create-account form (adult-only). Used by the billing panel
// and the Lumi Plus page sidebar.
//
// Age verification is PART of account creation, not a prerequisite the user must
// go do elsewhere. On "Create account" the form gets an age token automatically:
// it reuses a stored proof if there is one, accepts a self-declared token in dev,
// and otherwise runs the selfie verification right here in the form -- the camera
// panel only appears if that selfie step is actually needed. Calls onDone(account)
// on success. ASCII only.
// ===========================================================================

import { t } from '../i18n.js';
import { el, toast } from '../dom.js';
import { getFingerprint } from '../device-signal.js';
import { ensureAgeToken, decodeAgeToken } from '../age.js';

export function renderAuth(account, onDone, opts = {}) {
  let mode = opts.mode === 'login' ? 'login' : 'register';
  let cancelled = false;

  // A placeholder is not a name: it goes away the moment somebody types,
  // and several screen readers never announce it. Every field carries an
  // aria-label, and the autocomplete that tells a password manager what it
  // is looking at.
  const username = el('input', { type: 'text', placeholder: t('Username'), maxlength: '24', autocomplete: 'username', aria: { label: 'Username' } });
  const password = el('input', { type: 'password', placeholder: t('Password (8+ characters)'), autocomplete: 'current-password', aria: { label: 'Password' } });
  const year = el('input', { type: 'number', placeholder: t('Birth year'), min: '1900', max: '2026', autocomplete: 'bday-year', aria: { label: 'Birth year' } });
  const err = el('div', { class: 'error' });
  const tabs = el('div', { class: 'auth-tabs' });
  const claimMsg = el('div', { class: 'claim-msg' });
  const yearWrap = el('div', { class: 'year-wrap' });
  const submitBtn = el('button', { class: 'btn btn-primary' });

  const formEl = el('div', { class: 'auth-fields' }, [tabs, claimMsg, username, password, yearWrap, err, submitBtn]);

  // Verify panel: built once, revealed only if the selfie step actually runs.
  const verifyVideo = el('video', { autoplay: '', playsinline: '', muted: '', class: 'verify-video' });
  // These two change on their own while the check runs, so they announce
  // themselves. This is what aria-live on #app was reaching for, without
  // making every other change on the site an announcement too.
  const verifyTitle = el('div', { class: 'verify-title', role: 'status' });
  const verifySub = el('div', { class: 'verify-sub', role: 'status' });
  const verifyEl = el('div', { class: 'verify-panel hidden' }, [
    // This has to match what the server actually does. It used to say the photo
    // was "deleted, and never stored", which stopped being true the moment
    // checks were kept so the age model could be audited -- and a promise that
    // is not kept is worse than one never made.
    el('p', {
      class: 'side-sub',
      text:
        'We confirm your age with a quick selfie. It is never shown to anyone you chat with. ' +
        'A moderator may review it, and it is kept for up to 90 days so we can check our age ' +
        'checking is working, then deleted. You only do this once a day.',
    }),
    verifyVideo,
    verifyTitle,
    verifySub,
    el('button', { class: 'btn btn-ghost btn-sm', text: t('Cancel'), onClick: () => { cancelled = true; doneVerify(); } }),
  ]);
  // Where the recovery flow renders. Kept beside the form rather than inside
  // it so switching between them cannot disturb the form's own state.
  const panelWrap = el('div', {});
  const forgotBtn = el('button', {
    class: 'btn btn-ghost btn-sm auth-forgot',
    text: t('Forgot your password?'),
    onClick: () => showRecoverForm(),
  });
  const forgotWrap = el('div', {});
  const wrap = el('div', { class: 'auth-form' }, [formEl, forgotWrap, verifyEl, panelWrap]);

  let verifyShown = false;
  function showPanel() {
    if (verifyShown) return;
    verifyShown = true;
    formEl.classList.add('hidden');
    verifyEl.classList.remove('hidden');
  }
  function doneVerify(errorMsg) {
    verifyShown = false;
    verifyEl.classList.add('hidden');
    formEl.classList.remove('hidden');
    submitBtn.disabled = false;
    submitBtn.textContent = mode === 'login' ? t('Sign in') : t('Create account (18+)');
    if (errorMsg) err.textContent = errorMsg;
  }

  const verifyUi = {
    onVideo: (s) => {
      showPanel();
      verifyVideo.srcObject = s;
      verifyVideo.play?.().catch(() => {});
    },
    onStatus: (titleText, s) => {
      showPanel();
      verifyTitle.textContent = titleText;
      verifySub.textContent = s || '';
    },
    isCancelled: () => cancelled,
  };

  const doLogin = async () => {
    err.textContent = '';
    const { status, data } = await account.login(username.value.trim(), password.value);
    if (status === 200) {
      toast('Signed in.');
      onDone?.(account);
    } else if (status === 409 && data.error === 'claim_required') {
      // An admin pre-granted this username -> finish setup by choosing a password.
      mode = 'claim';
      syncMode();
    } else {
      err.textContent = 'Wrong username or password.';
    }
  };

  const doClaim = async () => {
    err.textContent = '';
    if (password.value.length < 8) {
      err.textContent = 'Choose a password of at least 8 characters.';
      return;
    }
    const by = Number(year.value);
    if (!by) {
      err.textContent = 'Enter your birth year to confirm you are 18+.';
      return;
    }
    cancelled = false;
    submitBtn.disabled = true;
    submitBtn.textContent = 'Confirming your age...';
    let ageToken;
    try {
      const fingerprint = await getFingerprint();
      ageToken = await ensureAgeToken(by, fingerprint, verifyUi);
    } catch (e) {
      if (String(e?.message) === 'cancelled') doneVerify();
      else doneVerify(e?.message || 'Age check could not be completed.');
      return;
    }
    if (decodeAgeToken(ageToken)?.band !== 'adult') {
      doneVerify('You must be 18 or older to claim Lumi Plus.');
      return;
    }
    const { status, data } = await account.claim(username.value.trim(), password.value, ageToken);
    if (status === 200) {
      toast('Welcome to Lumi Plus!');
      onDone?.(account);
    } else {
      doneVerify(
        data.error === 'already_claimed'
          ? 'This account is already set up: just sign in.'
          : data.error === 'adult_only'
            ? 'Lumi Plus accounts are 18+ only.'
            : 'Could not finish setting up your account.',
      );
    }
  };

  const doRegister = async () => {
    err.textContent = '';
    if (!/^[a-z0-9_.-]{3,24}$/i.test(username.value.trim())) {
      err.textContent = 'Pick a username (3-24 letters, digits, _ . -).';
      return;
    }
    if (password.value.length < 8) {
      err.textContent = 'Password must be at least 8 characters.';
      return;
    }
    const by = Number(year.value);
    if (!by) {
      err.textContent = 'Enter your birth year (you must be 18+).';
      return;
    }
    cancelled = false;
    submitBtn.disabled = true;
    submitBtn.textContent = 'Confirming your age...';
    let ageToken;
    try {
      const fingerprint = await getFingerprint();
      ageToken = await ensureAgeToken(by, fingerprint, verifyUi);
    } catch (e) {
      if (String(e?.message) === 'cancelled') doneVerify();
      else doneVerify(e?.message || 'Age check could not be completed.');
      return;
    }
    if (decodeAgeToken(ageToken)?.band !== 'adult') {
      doneVerify('You must be 18 or older to create an account.');
      return;
    }
    const { status, data } = await account.register(username.value.trim(), password.value, ageToken);
    if (status === 200) {
      // The one moment this code exists in readable form. There is no email on
      // a Lumi account, so if they walk past this screen without keeping it,
      // a forgotten password means asking a human who cannot verify them.
      // Hence a step they have to acknowledge rather than a toast they can miss.
      if (data.recoveryCode) showRecoveryCode(data.recoveryCode, () => onDone?.(account));
      else {
        toast('Account created.');
        onDone?.(account);
      }
    } else {
      doneVerify(
        data.error === 'username_taken'
          ? 'That username is taken.'
          : data.error === 'weak_password'
            ? 'Password must be at least 8 characters.'
            : data.error === 'adult_only'
              ? 'Accounts are 18+ only.'
              : 'Could not create the account.',
      );
    }
  };

  function syncMode() {
    if (mode === 'claim') {
      tabs.replaceChildren();
      claimMsg.textContent =
        'An admin set you up with Lumi Plus! Choose a password to finish: you will use it to sign in from now on.';
      username.disabled = true;
      password.placeholder = 'Choose a password (8+ characters)';
      password.autocomplete = 'new-password';
      yearWrap.replaceChildren(year);
      submitBtn.disabled = false;
      submitBtn.textContent = 'Claim Lumi Plus';
      submitBtn.onclick = doClaim;
      err.textContent = '';
      return;
    }
    username.disabled = false;
    claimMsg.textContent = '';
    password.placeholder = 'Password (8+ characters)';
    tabs.replaceChildren(
      el('button', { class: `tab ${mode === 'login' ? 'on' : ''}`, text: t('Sign in'), onClick: () => { mode = 'login'; syncMode(); } }),
      el('button', { class: `tab ${mode === 'register' ? 'on' : ''}`, text: t('Create account'), onClick: () => { mode = 'register'; syncMode(); } }),
    );
    yearWrap.replaceChildren(...(mode === 'register' ? [year] : []));
    password.autocomplete = mode === 'register' ? 'new-password' : 'current-password';
    submitBtn.disabled = false;
    submitBtn.textContent = mode === 'login' ? t('Sign in') : t('Create account (18+)');
    submitBtn.onclick = mode === 'login' ? doLogin : doRegister;
    // Only offered on the sign-in tab: it is the only place somebody arrives
    // already knowing they cannot get in.
    forgotWrap.replaceChildren(...(mode === 'login' ? [forgotBtn] : []));
    err.textContent = '';
  }

  // ---- The recovery code, shown exactly once --------------------------------
  //
  // A step, not a notice. There is no email on a Lumi account and only a hash of
  // this code is kept, so nobody -- including us -- can produce it again. If it
  // is not written down here, a forgotten password later means asking an admin
  // who has no way to prove who is asking.
  function showRecoveryCode(code, done) {
    const codeEl = el('code', { class: 'recovery-code', text: code });
    const ack = el('input', { type: 'checkbox', id: 'rec-ack' });
    const go = el('button', { class: 'btn btn-primary', text: t('Continue'), disabled: true });
    ack.onchange = () => { go.disabled = !ack.checked; };
    go.onclick = () => { panel.remove(); done(); };

    const panel = el('div', { class: 'recovery-panel' }, [
      el('h3', { text: t('Write this down before you continue') }),
      el('p', { class: 'muted', text:
        'This is the only way back into your account if you forget your password. '
        + 'Lumi has no email address for you, so there is nothing to send a reset link to.' }),
      codeEl,
      el('div', { class: 'recovery-actions' }, [
        el('button', {
          class: 'btn btn-ghost btn-sm',
          text: t('Copy'),
          onClick: async () => {
            try {
              await navigator.clipboard.writeText(code);
              toast('Copied. Keep it somewhere you will still have it in a year.');
            } catch {
              // Clipboard access is refused often enough that failing silently
              // would look like a broken button.
              toast('Could not copy. Write it down instead.');
            }
          },
        }),
      ]),
      el('label', { class: 'recovery-ack' }, [ack, el('span', { text: t(' I have written it down or saved it') })]),
      go,
    ]);
    wrap.append(panel);
  }

  // ---- Lost the password ----------------------------------------------------
  function showRecoverForm() {
    let attemptsLeft = null;
    const u = el('input', { type: 'text', class: 'auth-input', placeholder: t('Username'), autocomplete: 'username', aria: { label: 'Username' } });
    u.value = username.value.trim();
    const c = el('input', { type: 'text', class: 'auth-input', placeholder: t('Recovery code from signup'), autocomplete: 'off', spellcheck: 'false', aria: { label: 'Recovery code from signup' } });
    const pw = el('input', { type: 'password', class: 'auth-input', placeholder: t('New password (8+ characters)'), autocomplete: 'new-password', aria: { label: 'New password' } });
    const msg = el('div', { class: 'auth-err' });
    const askBtn = el('button', { class: 'btn btn-ghost btn-sm', text: t('Report to an admin') });
    const askWrap = el('div', {});
    const submit = el('button', { class: 'btn btn-primary', text: t('Set a new password') });

    askBtn.onclick = async () => {
      askBtn.disabled = true;
      await account.recoveryHelp(u.value.trim(), await getFingerprint().catch(() => ''));
      // Deliberately says nothing about whether the account exists. A real
      // person loses nothing by this; somebody fishing for usernames learns
      // nothing from it.
      askWrap.replaceChildren(el('p', { class: 'muted', text:
        'Sent. Open the Support tab and keep the page open, and an admin will reply there. '
        + 'They cannot prove who you are, so they may ask you things only you would know.' }));
    };

    submit.onclick = async () => {
      msg.textContent = '';
      submit.disabled = true;
      const { status, data } = await account.recover(
        u.value.trim(), c.value.trim(), pw.value, await getFingerprint().catch(() => ''),
      );
      submit.disabled = false;
      if (status === 200) {
        if (data.recoveryCode) showRecoveryCode(data.recoveryCode, () => onDone?.(account));
        else onDone?.(account);
        return;
      }
      if (data.error === 'weak_password') {
        msg.textContent = 'Password must be at least 8 characters.';
        return;
      }
      attemptsLeft = typeof data.attemptsLeft === 'number' ? data.attemptsLeft : attemptsLeft;
      if (data.exhausted || attemptsLeft === 0) {
        // Not a security boundary -- the code has far too much entropy to guess.
        // This is the point where somebody who has genuinely lost it should be
        // handed a person rather than left trying the same thing.
        msg.textContent = 'That code was not right, and there are no more tries.';
        c.disabled = true;
        submit.disabled = true;
        askWrap.replaceChildren(
          el('p', { class: 'muted', text: t('If you have lost the code as well, an admin can look at it.') }),
          askBtn,
        );
        return;
      }
      msg.textContent = attemptsLeft === null
        ? 'That did not work.'
        : `That code was not right. ${attemptsLeft} ${attemptsLeft === 1 ? 'try' : 'tries'} left.`;
    };

    panelWrap.replaceChildren(
      el('h3', { text: t('Use your recovery code') }),
      el('p', { class: 'muted', text: t('The code you were given when you created the account.') }),
      u, c, pw, msg, submit, askWrap,
      el('button', { class: 'btn btn-ghost btn-sm', text: t('Back to sign in'), onClick: () => { panelWrap.replaceChildren(); formEl.hidden = false; } }),
    );
    formEl.hidden = true;
  }

  syncMode();
  return wrap;
}

export default { renderAuth };
